import { AccordionComponent, AccordionItemDirective, AccordionItemsDirective } from '@syncfusion/ej2-react-navigations'
import ReactDOMServer from 'react-dom/server'
import styles from './videoStructureAccordion.module.scss'
import { useRef } from 'react'
import { ToggleAllAccordionBtn } from './ToggleAllAccordionBtn'
import { useAppSelector } from '../../redux/_reduxHooks.tsx'
import { NwTopicChapter } from '../../types/webTypes/NwTopicChapter.tsx'
import { NwTopicLesson } from '../../types/webTypes/NwTopicLesson.tsx'
import { WebThemaDto } from '../../types/webTypes/WebThemaDto.tsx'

export const VideoStructureAccordion = () => {
  const accInstance = useRef<AccordionComponent | null>(null)
  const webTopic = useAppSelector((state) => state.web.selectedNwTopic)

  function intToHourMinute(durationInMinutes: number): string {
    const hours = Math.floor(durationInMinutes / 60)
    const minutes = durationInMinutes % 60

    // Pad minutes with leading zero if necessary
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`

    return hours > 0 ? `${hours} Std. ${formattedMinutes} Min.` : `${formattedMinutes} Min.`
  }

  // GesamtlessonCount über alle Kapitel ermitteln
  const totalLessonCount =
    webTopic?.chapters.reduce((total, chapter) => {
      const lessonCount = chapter.topicLessons?.length ?? 0
      return total + lessonCount
    }, 0) ?? 0

  function formatDuration(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours} Std. ${minutes} Min.`
  }

  function calculateTotalDuration(book: WebThemaDto | undefined): string {
    if (!book || !book.chapters) {
      return '0 Std. 0 Min.'
    }

    const totalDuration = book.chapters.reduce((acc, chapter) => {
      const chapterTotalDuration = (chapter.topicLessons ?? []).reduce((lessonAcc, lesson) => {
        return lessonAcc + (lesson.duration ?? 0)
      }, 0)
      return acc + chapterTotalDuration
    }, 0)

    return formatDuration(totalDuration)
  }

  function calculateTotalDurationForChapter(chapter: NwTopicChapter | null | undefined): string {
    if (!chapter || !chapter.topicLessons) {
      return '0 Std. 0 Min.'
    }

    const totalDuration = chapter.topicLessons.reduce((lessonAcc, lesson) => {
      return lessonAcc + (lesson.duration ?? 0)
    }, 0)

    return formatDuration(totalDuration)
  }

  const chapterCountAndDuration = (chapter: NwTopicChapter) => {
    let lessonText = chapter.topicLessons?.length === 1 ? 'Lektion' : 'Lektionen'
    return `${chapter.topicLessons?.length} ${lessonText} • ${calculateTotalDurationForChapter(chapter)}`
  }

  const accHeaderMapping = (chapter: NwTopicChapter) => {
    const headerElement = (
      <>
        <div className={styles.headerOuter}>
          <div className={styles.title}>{chapter.title}</div>
          {/*ToDo#270:Info als Hover für die Description einfügen*/}
          <div className={styles.length}>{chapterCountAndDuration(chapter)}</div>
        </div>
      </>
    )

    // Convert the React element to a string
    const headerString = ReactDOMServer.renderToString(headerElement)

    return headerString
  }


  // Accordion Kapitel Inhalte mappen
  const accContentMapping = (chapter: NwTopicChapter) => {
    const sortedLessons = Array.isArray(chapter.topicLessons) ? [...chapter.topicLessons].sort((a, b) => a.position - b.position) : []
    const ulElement = (
      <>
        <ul className={styles.list}>
          {(sortedLessons.length > 0) ? (
            sortedLessons.map((section: NwTopicLesson) => (
              <li key={section.id} className={styles.listItem}>
                <span className={styles.listRow}>
                  <span className={`e-icons ${section.icon}`}></span>
                  <span>{section.title}</span>
                </span>
                {section.duration !== null && <span>{intToHourMinute(section.duration)}</span>}
              </li>
            ))
          ) : (
            <li className={styles.listItem}>Keine Lektionen verfügbar</li>
          )}
        </ul>
      </>
    )

    // Convert the React element to a string
    const ulString = ReactDOMServer.renderToString(ulElement)

    return ulString
  }

  const exapndAllChapters = () => {
    accInstance.current?.expandItem(true)
  }

  const collapseAllChapters = () => {
    accInstance.current?.expandItem(false)
  }

  const totalSectionAndDurationCount = () => {
    let chapterText = 'Kapitel'
    let lessonText = totalLessonCount === 1 ? 'Lektion' : 'Lektionen'

    return `${webTopic?.chapters.length} ${chapterText} • ${totalLessonCount} ${lessonText} • ${calculateTotalDuration(webTopic)} Gesamtdauer`
  }

  const sortedChapters = Array.isArray(webTopic?.chapters) ? [...webTopic?.chapters].sort((a, b) => a.position - b.position) : []

  return (
    <>
      {(sortedChapters.length > 0) && (
        <>
          <div>
            <b>Kursinhalt</b>
            <div className={styles.videoStructureStats}>
              <div className={styles.stats}>{totalSectionAndDurationCount()}</div>
              <ToggleAllAccordionBtn expandAll={exapndAllChapters} collapseAll={collapseAllChapters} />
            </div>
          </div>
          <AccordionComponent ref={accInstance} className={styles.videoContentAcc}>
            <AccordionItemsDirective>
              {sortedChapters.map((item) => (
                <AccordionItemDirective key={item.id} header={accHeaderMapping(item)} content={accContentMapping(item)} expanded={true} />
              ))}
            </AccordionItemsDirective>
          </AccordionComponent>
        </>
      )}
    </>
  )
}
